import { useEffect, useState } from 'react';

import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useHistory } from 'react-router-dom';

import * as S from './styled';

import { getMeterDeviceLists } from '../../../../hooks/smartcharging';
import {
  deleteVirtualGroup,
  getVirtualGroups,
} from '../../../../hooks/virtualgroup';
import { COLORS } from '../../../../utils/theme/colors';

type AlignType = 'left' | 'right' | 'center';

export const useVirtualGroupListPage = () => {
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  const [virtualGroup, setVirtualGroup] = useState<any>({
    error: false,
    data: null,
    loading: false,
    request: tryToGetVirtualGroups,
    pagination: { page: 1, pageSize: 10 },
  });

  const user = JSON.parse(localStorage.getItem('auth-user'));

  const shouldShowSmartCharging = user?.permissions.some(
    (permission) =>
      permission.role_id === 'DASH_SMART_CHARGING_CREATE' &&
      !!permission.status,
  );

  const columns: ColumnsType<any> = [
    {
      title: 'Título',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Qtd. de carregadores',
      dataIndex: 'qtd_charger_boxes',
      key: 'qtd_charger_boxes',
    },
    {
      title: 'Perfil de precificação',
      dataIndex: 'profile_pricing',
      key: 'profile_pricing',
    },
    ...(shouldShowSmartCharging
      ? [
          {
            title: 'Smart Charging',
            dataIndex: 'smart_charging',
            key: 'smart_charging',
          },
        ]
      : []),
    ...(shouldShowSmartCharging
      ? [
          {
            title: 'Medidor',
            dataIndex: 'meterDeviceStatus',
            key: 'meterDeviceStatus',
            align: 'center' as AlignType,
            render: (value) => {
              if (value !== 0 && value !== 1) {
                return <p>-</p>;
              }
              return (
                <S.MeterDeviceStatus
                  backgroundColor={value === 1 ? COLORS.autonomy : '#ffda29'}
                />
              );
            },
          },
        ]
      : []),
    {
      title: 'Excluir',
      dataIndex: 'remove',
      key: 'remove',
      align: 'center' as AlignType,
      render: (_, value) => (
        <S.RemoverButton
          danger
          ghost
          icon={<DeleteOutlined />}
          shape='circle'
          onClick={() => {
            confirm(value);
          }}
        />
      ),
      onCell: () => ({
        onClick: (event) => {
          event.stopPropagation();
        },
      }),
    },
  ];

  const modalDeleteVirtualGroupSuccess = () => {
    Modal.success({
      title: 'A configuração foi excluído com sucesso!',
      onOk: async () => {
        await tryToGetVirtualGroups();
      },
    });
  };

  const modalDeleteVirtualGroupError = () => {
    Modal.error({
      title: 'Ocorreu um erro ao excluir a configuração!',
    });
  };

  const confirm = (value: any) => {
    Modal.confirm({
      title: `Deseja excluir a configuração ${value?.title}?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Excluir',
      cancelText: 'Cancelar',
      okButtonProps: {
        danger: true,
      },
      onOk: async () => {
        try {
          await deleteVirtualGroup(value.id);

          modalDeleteVirtualGroupSuccess();
        } catch {
          modalDeleteVirtualGroupError();
        }
      },
    });
  };

  async function tryToGetVirtualGroups(
    page: number = 1,
    pageSize: number = 10,
    searchValue?: string,
  ) {
    setLoading(true);

    try {
      const { data } = await getVirtualGroups(page, pageSize, searchValue);

      // Sem o status do medidor os grupos continuam listados; a coluna Medidor fica com "-"
      let meterDeviceList;
      try {
        meterDeviceList = await getMeterDeviceLists(
          data.content
            .filter((value) => value.meter_device_remote_id)
            .map((value) => value.meter_device_remote_id),
        );
      } catch {
        meterDeviceList = undefined;
      }

      setVirtualGroup((value) => ({
        ...value,
        data: data.content.map((value, key) => {
          const meterDeviceStatus = meterDeviceList?.data.find(
            (meterDevice) =>
              value.meter_device_remote_id === meterDevice.device_remote_id,
          )?.meter?.communication_status;

          return {
            key: `virtual-groups-${key}`,
            id: value.virtual_group_id,
            title: value.name,
            qtd_charger_boxes: `${value.charge_box_count} carregador(es)`,
            profile_pricing: value.dynamic_price_enabled
              ? 'Habilitado'
              : 'Desabilitado',
            smart_charging: value.smart_charging_enabled
              ? 'Habilitado'
              : 'Desabilitado',
            meterDeviceStatus,
          };
        }),
        pagination: {
          page,
          pageSize,
          total: data.totalElements,
        },
      }));
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }

  useEffect(() => {
    (async () => {
      await tryToGetVirtualGroups();
    })();
  }, []);

  return {
    columns,
    virtualGroup,
    loading,
    history,
  };
};
